const debounce = <F extends (...args: Parameters<F>) => ReturnType<F>>(
  fn: F,
  wait = 500
): ((...args: Parameters<F>) => void) & { cancel: () => void } => {
  let timeoutId: number

  const debounced = (...args: Parameters<F>): void => {
    window.clearTimeout(timeoutId)
    timeoutId = window.setTimeout(() => fn(...args), wait)
  }

  debounced.cancel = (): void => window.clearTimeout(timeoutId)

  return debounced
}

const debounceAsync = <F extends (...args: Parameters<F>) => ReturnType<F>>(
  fn: F,
  wait = 500
): ((...args: Parameters<F>) => Promise<void>) => {
  let timeoutId: number

  return async (...args: Parameters<F>): Promise<void> => {
    window.clearTimeout(timeoutId)
    timeoutId = window.setTimeout(() => fn(...args), wait)
  }
}

const hasFocusedElement = (element: Element, relatedTarget: EventTarget | null): boolean =>
  element.contains((relatedTarget as Node) || document.activeElement)

const isTopmostElement = (element: HTMLElement): boolean => {
  const { bottom, left, right, top } = element.getBoundingClientRect()

  // Get topmost elements at the corners of `element`, then check if the latter contains
  // the former; if it doesn't, that means something else is positioned on top of the element.
  const corners = [
    { x: left + 1, y: top + 1 },
    { x: right - 1, y: top + 1 },
    { x: left + 1, y: bottom - 1 },
    { x: right - 1, y: bottom - 1 }
  ]

  return corners.every(({ x, y }) => element.contains(document.elementFromPoint(x, y)))
}

const setInputValueWithEvents = (
  input: HTMLInputElement,
  value: string,
  events = ["input", "change"]
): void => {
  input.value = value
  events.forEach((event) => input.dispatchEvent(new Event(event, { bubbles: true })))
}

const tryParseJSON = (json: object | string): object => {
  if (typeof json !== "string") {
    return json
  }

  try {
    return JSON.parse(json)
  } catch {
    return json as unknown as object
  }
}

// unindent a multiline string
const unindent = (str: string): string => str.replace(/\s{2,}/g, "\n\n").trim()

export {
  debounce,
  debounceAsync,
  hasFocusedElement,
  isTopmostElement,
  setInputValueWithEvents,
  tryParseJSON,
  unindent
}
